import React from 'react'
import { Image, Linking, StatusBar, StyleSheet, View } from 'react-native'

import { useAgentDetailsWF } from '@zet/customer'
import {
  B4,
  B5,
  B6,
  colors,
  H6,
  Icon,
  SH1,
  SH3,
  Stack,
  XStack,
  YStack,
} from '@zet/design-system'
import Avatar from '@zet/design-system/avatar/Avatar'
import Pressable from '@zet/design-system/pressable'
import BrandCardEligiblity from '@zet/feature-brands/components/brandCardV2/brandCardEligiblity'
import FeeHighlight from '@zet/feature-brands/components/brandCardV2/feeHighlight'
import { i18n } from '@zet/localization'
import InterestRateIcon from '@zet/svg/loanBrandCard/interestRate.svg'
import MoneyBag from '@zet/svg/loanBrandCard/moneyBag.svg'
import OfferTag from '@zet/svg/offerTag.svg'
import { getName } from '@zet/utils/stringConversion'
import { LinearGradient } from 'expo-linear-gradient'

import { useGetApplicationDetails } from '../../../api/applicationDetails/hook'

const LoanHeaders = ({ hash, variantRecommendationCard }) => {
  const { data: applicationDetails } = useGetApplicationDetails(hash)

  const { data: agentData } = useAgentDetailsWF()

  const leadSource = applicationDetails?.leadSource

  const getIcon = (type: string) => {
    switch (type) {
      case 'bag':
        return <MoneyBag />
      case 'interestRate':
        return <InterestRateIcon />
      default:
        return <OfferTag />
    }
  }
  return (
    <>
      <StatusBar backgroundColor={colors.tertiary[200]} />
      <LinearGradient
        colors={[colors.tertiary[100], colors.tertiary[100]]}
        start={[0, 0]}
        end={[1, 0.0]}
        locations={[0.0, 1.0]}
      >
        <Stack
          className="border-t"
          style={{ borderTopColor: colors.tertiary[20] }}
        >
          <Stack className="bg-tertiary-200 py-3">
            <XStack className="mx-3 items-center justify-between">
              <XStack className="items-center">
                <Avatar
                  name={
                    leadSource === 'plApp'
                      ? agentData?.customer?.fullName
                      : agentData?.fullName
                  }
                  size="large"
                />
                <Stack className="ml-2">
                  {Boolean(
                    leadSource === 'plApp'
                      ? agentData?.customer?.fullName
                      : agentData?.fullName
                  ) && (
                    <SH1
                      className="ml-[10] break-words text-white"
                      style={{ width: 200 }}
                    >
                      {getName(
                        leadSource === 'plApp'
                          ? agentData?.customer?.fullName + ''
                          : agentData?.fullName + ''
                      )}
                    </SH1>
                  )}
                  {Boolean(
                    leadSource === 'plApp'
                      ? agentData?.customer?.phoneNumber
                      : agentData?.phoneNumber
                  ) && (
                    <B6 className="text-grays-40 ml-[10] break-words">
                      {leadSource === 'plApp'
                        ? agentData?.customer?.phoneNumber
                        : agentData?.phoneNumber}
                    </B6>
                  )}
                </Stack>
              </XStack>
              <XStack className="items-center gap-2">
                <Pressable
                  className="border-grays-60 rounded border p-1"
                  onPress={() => {
                    Linking.openURL(
                      `tel:${
                        leadSource === 'plApp'
                          ? agentData?.customer?.phoneNumber
                          : agentData?.phoneNumber
                      }`
                    )
                  }}
                  eventName="click_action"
                  eventPayload={{
                    item_name: `pl_loan_call_customer`,
                  }}
                >
                  <Icon type="PhoneFilled" color="#F5F7FB" />
                </Pressable>
              </XStack>
            </XStack>
          </Stack>
        </Stack>
      </LinearGradient>

      {/* Bottom card */}

      <View className="mx-3 mt-2">
        <Stack className="items-center">
          <Stack className={` w-full overflow-hidden`}>
            <LinearGradient
              colors={[
                variantRecommendationCard?.backgroundGradient?.[0] || '#B6D7E9',
                variantRecommendationCard?.backgroundGradient?.[1] || '#FFFFFF',
              ]}
              start={{ x: 0, y: 0 }}
              end={{ x: 0.5, y: 0.5 }}
              locations={[0.1, 0.4]}
              className={`w-full rounded-t-lg`}
            >
              <Stack className="p-4/2 pt-1">
                <XStack
                  className="min-h-16 justify-between"
                  style={{ minHeight: 70 }}
                >
                  <Stack className="w-3/5 justify-center">
                    <SH3 numberOfLines={1}>
                      {variantRecommendationCard?.groupName}
                    </SH3>
                    <H6 numberOfLines={1}>
                      {variantRecommendationCard?.variantName ||
                        variantRecommendationCard?.name ||
                        ''}
                    </H6>
                  </Stack>
                  <Stack className="w-1/3">
                    <Image
                      className="flex-1 rounded-md"
                      source={{
                        uri: variantRecommendationCard?.imageUrl,
                      }}
                      resizeMode="contain"
                    />
                  </Stack>
                </XStack>

                {Boolean(
                  variantRecommendationCard?.variantApprovalDetails
                    ?.approvalRateTextColor
                ) && (
                  <BrandCardEligiblity
                    approvalRate={
                      variantRecommendationCard?.variantApprovalDetails
                    }
                  />
                )}

                {(variantRecommendationCard?.joiningFees ||
                  variantRecommendationCard?.renewalFees) && (
                  <XStack style={{ columnGap: 12 }} className="mt-4/2">
                    {variantRecommendationCard?.joiningFees && (
                      <FeeHighlight
                        title={i18n.t('brandCard.joiningFee')}
                        titlePostfix={
                          variantRecommendationCard?.isSpendBasedWaiver
                            ? '*'
                            : ''
                        }
                        amount={variantRecommendationCard?.joiningFees}
                      />
                    )}
                    {variantRecommendationCard?.renewalFees && (
                      <FeeHighlight
                        title={i18n.t('brandCard.renewalFee')}
                        amount={variantRecommendationCard?.renewalFees}
                      />
                    )}
                  </XStack>
                )}

                {Boolean(variantRecommendationCard?.isSpendBasedWaiver) && (
                  <XStack className="mt-2">
                    <B5 className="text-grays-70">
                      *{i18n.t('brandCard.spendBasedWaiver')}
                    </B5>
                  </XStack>
                )}

                {Boolean(variantRecommendationCard?.benefits?.length) && (
                  <YStack className="gap-y-2 pt-1">
                    {variantRecommendationCard?.benefits?.map(
                      (benefit, index) => {
                        return (
                          <XStack className="items-center" key={index}>
                            {getIcon(benefit?.icon)}

                            {benefit?.subText?.map((text) => {
                              return (
                                <B4
                                  className="text-grays-70 ml-1 flex-1"
                                  key={text}
                                >
                                  {text}
                                </B4>
                              )
                            })}
                          </XStack>
                        )
                      }
                    )}
                  </YStack>
                )}
              </Stack>
            </LinearGradient>
          </Stack>
        </Stack>
      </View>
    </>
  )
}

export default LoanHeaders

const styles = StyleSheet.create({})
